// Home.tsx
import React, { useState } from 'react';
import Hero from '../components/Hero/Hero';
import Pathfinder from '../components/Pathfinder/Pathfinder';
import Card from '../components/Card/Card';
import Wallpaper from '../components/Wallpaper/Wallpaper';
import Section from '../components/Section/Section';
import Accordion from '../components/Accordion/Accordion';
import GlobalCallout from '../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Image from '../components/Image/Image';
import ListItem from '../components/ListItem/ListItem';
import ContentSplit from '../components/ContentSplit/ContentSplit';
import Button from '../components/Button/Button';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Hero
        id="page-hero"
        headline="AI/ML-powered solutions for our government and <span class='nowrap'>commercial clients.</span>"
      >
        <Box id="home-hero-card-wrapper">
          <Typography className="industry-select action-on-view action-fade-in action-delay-250">
            Select your industry to discover tailored AI/ML solutions.
          </Typography>
          <Box className="card-group">
            <Card
              icon="assured_workload"
              headline="Government Solutions"
              subhead="Data-driven solutions supporting federal, state, and local agencies."
              id="home-hero-card-primary"
              type="home-hero"
              btnLabel="Government Data Solutions"
              btnURL="/industries/government"
              className="action-on-view action-fade-in action-delay-500"
            />
            <Card
              icon="domain"
              headline="Commercial Solutions"
              subhead="Scalable data solutions for diverse commercial applications."
              id="home-hero-card-secondary"
              type="home-hero"
              btnLabel="Commercial Data Solutions"
              btnURL="/industries/commercial"
              className="action-on-view action-fade-in action-delay-750"
            />
          </Box>
        </Box>
      </Hero>

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">Experience Innovation</h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics cutting-edge bioinformatics and AI
          solutions can transform your operations. Our services are tailored to
          your industry needs, showcasing how our innovative tools streamline
          workflows, unlock new insights, and empower data-driven decisions.
          Whether you&apos;re in government or commercial sectors, see firsthand
          how our services—like Curo46®, synthetic data (SYN46™), and
          de-identified datasets—help solve real-world challenges. Contacting us
          is your first step to understanding the impact we can deliver for your
          organization. Let&apos;s create a smarter and healthier future,
          together.
        </p>
        <Image
          path="/images/library/data-nodes-2.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="Data nodes."
        />
      </Section>

      <ContentSplit
        image="/images/library/woman-glasses-data.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Discover More</p>
        <h2 className="action-on-view action-fade-in">
          See How We Deliver Results
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Industry Solutions:</strong> Tailored AI/ML
            Solutions: Explore AI/ML solutions designed for your unique industry
            challenges and goals.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Data-Driven Decision Tools:</strong> Witness how advanced
            analytics simplify complex data into actionable insights.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Innovative AI Integration:</strong> See AI tools in action,
            automating tasks and enhancing efficiency.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Success Stories:</strong> Learn how organizations
            like yours achieved remarkable outcomes with us.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Explore More About General Genomics
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover in-depth insights into General Genomics&apos; services,
          innovations, and applications in various industries. Dive deeper into
          our offerings.
        </Typography>
        <Box className="card-group">
          <Card
            icon="science"
            headline="SYN46™"
            subhead="Discover SYN46™, your solution for innovative synthetic data challenges."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="engineering"
            headline="AI/ML @ General Genomics"
            subhead="Driving bioinformatics advancements and transforming bioscience innovation."
            btnURL="/innovation"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business_center"
            headline="Industries Served"
            subhead="Discover how our genomic solutions are transforming various industries."
            btnURL="/industries"
            btnLabel="Explore Industries"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="devices"
            headline="Connect With Us"
            subhead="Reach out to explore how our AI/ML solutions can address your unique challenges and drive innovation in your industry."
            btnURL="/contact-us"
            btnLabel="Contact us"
            className="action-on-view action-fade-in"
          />
        </Box>
      </Section>

      <Wallpaper asset="images/library/capital-building-digital.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover insights into General Genomics’ services, industries served,
          innovative AI solutions, and how they revolutionize bioinformatics.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline=" What industries does General Genomics serve?"
          >
            General Genomics provides tailored solutions for a range of
            industries, including{' '}
            <Link to="/industries/government">government</Link> and{' '}
            <Link to="/industries/commercial">commercial </Link>
            sectors, to address unique data challenges.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What services and solutions does General Genomics offer?"
          >
            Our expertise includes bioinformatics, synthetic data (SYN46™),
            de-identified data, AI-driven innovations, and specialized tools
            like <Link to="/innovation/curo46">Curo46®</Link> and Custom Data
            Solutions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics assist with synthetic and de-identified data?"
          >
            We develop secure, scalable solutions for generating synthetic and
            de-identified data, ensuring compliance and enabling advanced
            analytics across industries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ offerings?"
          >
            Explore our <Link to="/data-portal">Data Portal</Link>,{' '}
            <Link to="/industries">Industries</Link>, and{' '}
            <Link to="/innovation">Innovation</Link> sections, or contact us
            directly for tailored insights into how we can support, partner,
            partner with your business.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics build trust and credibility?"
          >
            We showcase client success stories, certifications, and
            industry-focused case studies to demonstrate our commitment to
            excellence and proven results.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default Home;
